import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Check, Tag, Zap, Crown, Clock } from "lucide-react";

export default function Subscription() {
  const { organization, fetchUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/subscription/status");
      setStatus(res.data);
      setPlans(res.data.plans || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    try {
      const res = await api.post("/subscription/apply-coupon", { code: couponCode });
      setAppliedCoupon(res.data.coupon);
      toast.success("Coupon applied!");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid coupon"); setAppliedCoupon(null); }
  };

  const getDiscountedPrice = (price) => {
    if (!appliedCoupon) return price;
    if (appliedCoupon.discountType === "percentage") return Math.max(0, price - (price * appliedCoupon.value / 100));
    return Math.max(0, price - appliedCoupon.value);
  };

  const handleSubscribe = async (plan) => {
    setPaying(plan.id);
    try {
      const orderRes = await api.post("/subscription/create-order", { planId: plan.id, couponCode: appliedCoupon?.code || "" });
      const { orderId, amount, currency, keyId } = orderRes.data;

      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: "Optimus Gym CRM",
        description: `${plan.name} Subscription`,
        order_id: orderId,
        handler: async function (response) {
          try {
            await api.post("/subscription/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planId: plan.id,
            });
            toast.success("Subscription activated!");
            fetchUser();
            fetchData();
          } catch { toast.error("Verification failed"); }
        },
        prefill: { email: organization?.email || "" },
        theme: { color: "#007AFF" },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", () => toast.error("Payment failed"));
        rzp.open();
      } else {
        toast.error("Razorpay not loaded. Please refresh.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create order"); }
    finally { setPaying(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  const statusConfig = {
    trial: { label: "Trial", color: "bg-blue-500/20 text-blue-400", icon: Clock },
    active: { label: "Active", color: "bg-green-500/20 text-green-400", icon: Check },
    expired: { label: "Expired", color: "bg-red-500/20 text-red-400", icon: Clock },
  };
  const sc = statusConfig[status?.subscriptionStatus] || statusConfig.trial;
  const StatusIcon = sc.icon;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">Subscription</h1>

      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${sc.color}`}>
                <StatusIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Status</p>
                <Badge className={`${sc.color} border-0 rounded-sm text-sm mt-1`}>{sc.label}</Badge>
              </div>
            </div>
            {status?.subscriptionStatus === "trial" && status?.trialEndDate && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Trial Ends</p>
                <p className="text-lg font-mono font-bold">{status.trialEndDate?.slice(0, 10)}</p>
              </div>
            )}
            {status?.subscriptionStatus === "active" && status?.subscriptionEndDate && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Subscription Ends</p>
                <p className="text-lg font-mono font-bold">{status.subscriptionEndDate?.slice(0, 10)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter coupon code" className="max-w-64 rounded-sm" data-testid="coupon-input" />
        <Button variant="outline" className="rounded-sm" onClick={handleApplyCoupon} data-testid="apply-coupon-btn"><Tag className="h-4 w-4 mr-1" />Apply</Button>
        {appliedCoupon && <Badge className="bg-green-500/20 text-green-400 border-0 rounded-sm">{appliedCoupon.discountType === "percentage" ? `${appliedCoupon.value}% off` : `\u20B9${appliedCoupon.value} off`}</Badge>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan, i) => {
          const discounted = getDiscountedPrice(plan.price);
          const isCurrentPlan = status?.planId === plan.id && status?.subscriptionStatus === "active";
          return (
            <Card key={plan.id} className={`bg-card border-border rounded-sm transition-all hover:border-primary/50 ${i === 1 ? "border-primary/30 relative" : ""}`} data-testid={`plan-card-${plan.id}`}>
              {i === 1 && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-primary text-primary-foreground border-0 rounded-sm"><Zap className="h-3 w-3 mr-1" />Popular</Badge></div>}
              <CardHeader className="pb-2">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />{plan.name}
                </CardTitle>
                {plan.description && <p className="text-sm text-muted-foreground">{plan.description}</p>}
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  {appliedCoupon && discounted < plan.price ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold font-heading">{"\u20B9"}{discounted.toLocaleString()}</span>
                      <span className="text-lg text-muted-foreground line-through">{"\u20B9"}{plan.price.toLocaleString()}</span>
                    </div>
                  ) : (
                    <span className="text-3xl font-bold font-heading">{"\u20B9"}{plan.price.toLocaleString()}</span>
                  )}
                  <span className="text-sm text-muted-foreground ml-1">/ {plan.billingCycle?.toLowerCase()}</span>
                </div>
                <div className="text-sm text-muted-foreground">{plan.trialDays} days free trial</div>
                {isCurrentPlan ? (
                  <Button disabled className="w-full rounded-sm" data-testid={`subscribe-${plan.id}`}><Check className="h-4 w-4 mr-1" />Current Plan</Button>
                ) : (
                  <Button className="w-full rounded-sm" onClick={() => handleSubscribe(plan)} disabled={!!paying} data-testid={`subscribe-${plan.id}`}>
                    {paying === plan.id ? "Processing..." : <><CreditCard className="h-4 w-4 mr-1" />Subscribe</>}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
        {plans.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-8">No plans available. Contact support.</p>}
      </div>
    </div>
  );
}
